/**
 * Devolver documentos — sección "Devoluciones" del hub de Inventario de
 * Documentos (transferenciadocumentos.js). Se monta embebida: el hub ya pone
 * la cabecera, por eso arranca directo con las secciones.
 *
 * Quita del blob `sucursaldocumentos` de un chasis los tipos de documento que
 * el usuario marca, dejando constancia en `devoluciondocumentos` con un
 * comentario obligatorio. Solo se puede devolver lo que está en sucursal y
 * todavía no se entregó al cliente: los entregados se muestran bloqueados y
 * los que nunca se recibieron aparecen como "no recibido" para que se vea el
 * estado completo del vehículo.
 *
 * Backend (transferenciabackend.py):
 *   POST /api/consultarmatricula {sucursal} → usa data.matriculas2 (chasis +
 *        blob sucursaldocumentos de todos los productos).
 *   POST /api/transferenciabackend_procesardevolucion
 *        {documentoadevolver: [tipos], chasis, comentario}.
 */
import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Checkbox from "@mui/material/Checkbox";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SearchOffOutlinedIcon from "@mui/icons-material/SearchOffOutlined";
import { useGlobalMDAlert } from "context";
import {
  FP,
  StatusPill,
  EstadoVacio,
  ConfirmarDialog,
  fpInputSx,
  fpMultilineInputSx,
} from "components/financepro";
import { useApi } from "lib/api";
import {
  estadoPorTipo,
  parseDocumentos,
  nombreSucursal,
  useTiposDocumento,
} from "lib/documentosvehiculo";

// matriculas2 no trae marca/modelo: la segunda línea del buscador usa placa y
// suplidor, que es lo que el backend devuelve para identificar el vehículo.
const filtrarChasis = createFilterOptions({
  stringify: (o) => `${o.chasis} ${o.placa || ""} ${o.suplidor || ""}`,
  limit: 60,
});

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}
SectionTitle.propTypes = {
  icon: PropTypes.elementType.isRequired,
  children: PropTypes.node.isRequired,
  color: PropTypes.string.isRequired,
};

// Estado de cada tipo del catálogo frente al blob del chasis elegido. Solo
// "disponible" se puede marcar; los otros dos se muestran para contexto.
const ESTADO_TIPO = {
  disponible: { label: "Disponible", variant: "success" },
  entregado: { label: "Entregado", variant: "warning" },
  norecibido: { label: "No recibido", variant: "neutral" },
};

const estadoDelTipo = (e) => {
  if (!e || !e.presente) return "norecibido";
  return e.entregado ? "entregado" : "disponible";
};

export default function DevolverMatricula() {
  const { post } = useApi();
  const { showAlert } = useGlobalMDAlert();
  const { tipos: tiposCatalogo } = useTiposDocumento();

  // Se tolera localStorage solo para mostrar la sucursal actual: el endpoint
  // de consulta la recibe como filtro y no la devuelve.
  const sucursal = localStorage.getItem("sucursal") || "";

  const [chasisLista, setChasisLista] = useState([]);
  const [cargandoChasis, setCargandoChasis] = useState(false);
  const [chasisSel, setChasisSel] = useState(null);
  const [seleccionados, setSeleccionados] = useState([]);
  const [comentario, setComentario] = useState("");
  const [procesando, setProcesando] = useState(false);
  const [confirmar, setConfirmar] = useState(false);

  const cargarChasis = async () => {
    setCargandoChasis(true);
    try {
      const { ok, data } = await post("/api/consultarmatricula", { sucursal });
      if (!ok) {
        showAlert({ message: data.Error || "No se pudieron cargar los chasis", color: "error" });
        return;
      }
      setChasisLista(data.matriculas2 || []);
    } catch (e) {
      showAlert({ message: `Error de conexión: ${e.message}`, color: "error" });
    } finally {
      setCargandoChasis(false);
    }
  };

  useEffect(() => {
    cargarChasis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Un tipo se puede devolver si está en el blob (recibido en sucursal) y
  // no se entregó al cliente; el resto se lista deshabilitado con su estado.
  const filas = useMemo(() => {
    if (!chasisSel) return [];
    const estado = estadoPorTipo(chasisSel.sucursaldocumentos, tiposCatalogo);
    return tiposCatalogo.map((tipo) => {
      const e = estado[tipo.value] || {};
      return { ...tipo, estado: estadoDelTipo(e), sucursalrecep: e.sucursalrecep || "" };
    });
  }, [chasisSel, tiposCatalogo]);

  const disponibles = filas.filter((f) => f.estado === "disponible");
  const todosMarcados = disponibles.length > 0 && seleccionados.length === disponibles.length;
  const labelsSeleccionados = seleccionados
    .map((v) => filas.find((f) => f.value === v)?.label || v)
    .join(", ");

  const elegirChasis = (_, v) => {
    setChasisSel(v);
    setSeleccionados([]);
  };

  const alternarTipo = (value, marcado) =>
    setSeleccionados((prev) => (marcado ? [...prev, value] : prev.filter((t) => t !== value)));

  const limpiar = () => {
    setChasisSel(null);
    setSeleccionados([]);
    setComentario("");
  };

  const pedirConfirmacion = () => {
    if (!chasisSel) {
      showAlert({ message: "Seleccione un chasis", color: "warning" });
      return;
    }
    if (seleccionados.length === 0) {
      showAlert({ message: "Marque al menos un documento para devolver", color: "warning" });
      return;
    }
    if (!comentario.trim()) {
      showAlert({ message: "Escriba el motivo de la devolución", color: "warning" });
      return;
    }
    setConfirmar(true);
  };

  const procesar = async () => {
    setProcesando(true);
    try {
      const { ok, data } = await post("/api/transferenciabackend_procesardevolucion", {
        documentoadevolver: seleccionados,
        chasis: chasisSel.chasis,
        comentario,
      });
      if (!ok) {
        showAlert({ message: data.Error || "No se pudo procesar la devolución", color: "error" });
        return;
      }
      showAlert({ message: `Devolución completada: ${labelsSeleccionados}`, color: "success" });
      setConfirmar(false);
      limpiar();
      // El blob cambió en el servidor: recargar para que el buscador lo refleje.
      cargarChasis();
    } catch (e) {
      showAlert({ message: `Error de conexión: ${e.message}`, color: "error" });
    } finally {
      setProcesando(false);
    }
  };

  const nombreSuc = nombreSucursal(sucursal);
  const documentosEnSucursal = chasisSel ? parseDocumentos(chasisSel.sucursaldocumentos).length : 0;

  return (
    <Box>
      {/* Vehículo: buscador por chasis sobre todos los productos del tenant */}
      <Box sx={sectionBox}>
        <SectionTitle icon={DirectionsCarFilledOutlinedIcon} color={FP.color.accentBlue}>
          Vehículo
        </SectionTitle>
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Autocomplete
              options={chasisLista}
              loading={cargandoChasis}
              value={chasisSel}
              onChange={elegirChasis}
              filterOptions={filtrarChasis}
              getOptionLabel={(o) => o.chasis || ""}
              isOptionEqualToValue={(o, v) => o.chasis === v.chasis}
              renderOption={(props, o) => (
                <li {...props} key={o.chasis}>
                  <Box>
                    <Typography
                      sx={{ fontFamily: "monospace", fontWeight: 700, fontSize: "0.85rem" }}
                    >
                      {o.chasis}
                    </Typography>
                    <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                      {[o.placa && `Placa ${o.placa}`, o.suplidor].filter(Boolean).join(" · ") ||
                        "Sin placa"}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Chasis, placa o suplidor"
                  sx={fpInputSx}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {cargandoChasis ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={7} sx={{ display: "flex", alignItems: "center" }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                label={`Sucursal ${nombreSuc || sucursal || "—"}`}
                sx={{ bgcolor: "#E3F2FD", color: "#1565C0", fontWeight: 700 }}
              />
              {chasisSel && (
                <>
                  {chasisSel.placa && (
                    <Chip
                      label={`Placa ${chasisSel.placa}`}
                      sx={{
                        bgcolor: FP.color.surfaceAlt,
                        color: FP.color.textSecondary,
                        fontWeight: 700,
                        fontFamily: "monospace",
                      }}
                    />
                  )}
                  {chasisSel.localizacion && chasisSel.localizacion !== "N" && (
                    <Chip
                      label={`Localización ${chasisSel.localizacion}`}
                      sx={{
                        bgcolor: FP.color.surfaceAlt,
                        color: FP.color.textSecondary,
                        fontWeight: 700,
                      }}
                    />
                  )}
                  <Chip
                    label={
                      documentosEnSucursal === 1
                        ? "1 documento en sucursal"
                        : `${documentosEnSucursal} documentos en sucursal`
                    }
                    sx={
                      documentosEnSucursal > 0
                        ? { bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 }
                        : { bgcolor: "#ECEFF1", color: "#455A64", fontWeight: 700 }
                    }
                  />
                </>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Box>

      {/* Documentos a devolver: un renglón por tipo del catálogo con su estado */}
      <Box sx={sectionBox}>
        <SectionTitle icon={DescriptionOutlinedIcon} color={FP.color.success}>
          Documentos a devolver
          {chasisSel && seleccionados.length > 0 ? ` (${seleccionados.length})` : ""}
        </SectionTitle>

        {!chasisSel && (
          <EstadoVacio
            compacto
            icono={SearchOffOutlinedIcon}
            titulo="Seleccione un chasis"
            subtitulo="Busque el vehículo por chasis, placa o suplidor para ver qué documentos están en sucursal y pueden devolverse a su origen."
          />
        )}

        {chasisSel && (
          <>
            {disponibles.length === 0 ? (
              <Typography sx={{ fontSize: "0.82rem", color: FP.color.textMuted, mb: 1.5 }}>
                Este chasis no tiene documentos devolvibles: los que están en sucursal ya fueron
                entregados al cliente o nunca se recibieron.
              </Typography>
            ) : (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                <Button
                  size="small"
                  onClick={() =>
                    setSeleccionados(todosMarcados ? [] : disponibles.map((f) => f.value))
                  }
                  sx={{ textTransform: "none", fontWeight: 700, color: FP.color.accentBlue }}
                >
                  {todosMarcados ? "Desmarcar todos" : "Marcar todos los disponibles"}
                </Button>
              </Box>
            )}
            <Grid container spacing={1.2}>
              {filas.map((f) => {
                const habilitado = f.estado === "disponible";
                const marcado = habilitado && seleccionados.includes(f.value);
                const pill = ESTADO_TIPO[f.estado];
                return (
                  <Grid item xs={12} sm={6} md={4} key={f.value}>
                    <Box
                      onClick={() => habilitado && alternarTipo(f.value, !marcado)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.6,
                        px: 1.2,
                        py: 0.8,
                        borderRadius: 1.8,
                        border: `1px solid ${marcado ? FP.color.navy : FP.color.border}`,
                        bgcolor: marcado ? FP.color.accentBlueSoft : FP.color.cardBg,
                        opacity: habilitado ? 1 : 0.7,
                        cursor: habilitado ? "pointer" : "not-allowed",
                        transition: "border-color 120ms, background-color 120ms",
                      }}
                    >
                      <Checkbox
                        size="small"
                        disabled={!habilitado}
                        checked={marcado}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => alternarTipo(f.value, e.target.checked)}
                        sx={{ p: 0.4 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.85rem",
                            color: habilitado ? FP.color.textPrimary : FP.color.textSecondary,
                          }}
                        >
                          {f.label}
                        </Typography>
                        {f.sucursalrecep && (
                          <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                            Recibido en {nombreSucursal(f.sucursalrecep) || f.sucursalrecep}
                          </Typography>
                        )}
                      </Box>
                      <StatusPill label={pill.label} variant={pill.variant} />
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}
      </Box>

      {/* Motivo y acción */}
      <Box sx={{ ...sectionBox, mb: 0 }}>
        <SectionTitle icon={UndoOutlinedIcon} color={FP.color.navy}>
          Motivo de la devolución
        </SectionTitle>
        <TextField
          label="Comentario (obligatorio)"
          value={comentario}
          onChange={(e) => setComentario(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          disabled={procesando}
          placeholder="Por qué se devuelven estos documentos a su sucursal de origen…"
          helperText="Queda registrado junto con el usuario, la fecha y los documentos devueltos."
          InputLabelProps={{ shrink: true }}
          sx={fpMultilineInputSx}
        />
        {seleccionados.length > 0 && (
          <Typography sx={{ mt: 1.5, fontSize: "0.82rem", color: FP.color.textSecondary }}>
            Se devolverán: <b>{labelsSeleccionados}</b> del chasis{" "}
            <Box component="span" sx={{ fontFamily: "monospace", fontWeight: 700 }}>
              {chasisSel?.chasis}
            </Box>
            .
          </Typography>
        )}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ mt: 2 }}>
          <Button
            variant="contained"
            onClick={pedirConfirmacion}
            disabled={!chasisSel || seleccionados.length === 0 || procesando}
            startIcon={
              procesando ? <CircularProgress size={16} color="inherit" /> : <UndoOutlinedIcon />
            }
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            {seleccionados.length === 1
              ? "Devolver 1 documento"
              : `Devolver ${seleccionados.length} documentos`}
          </Button>
          <Button
            variant="outlined"
            onClick={limpiar}
            disabled={procesando}
            startIcon={<RestartAltIcon />}
            sx={btnOutline(FP.color.textSecondary)}
          >
            Limpiar
          </Button>
        </Stack>
      </Box>

      <ConfirmarDialog
        abierto={confirmar}
        titulo="Confirmar devolución"
        mensaje={`Se quitarán de la sucursal ${
          seleccionados.length === 1 ? "el documento" : "los documentos"
        } ${labelsSeleccionados} del chasis ${
          chasisSel?.chasis || ""
        }. Motivo: "${comentario.trim()}".`}
        textoConfirmar="Devolver"
        destructivo
        cargando={procesando}
        onCerrar={() => setConfirmar(false)}
        onConfirmar={procesar}
      />
    </Box>
  );
}
